import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $insertNodes,
  $isRangeSelection,
  $setSelection,
  BaseSelection,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_LOW,
  EditorState,
  FORMAT_TEXT_COMMAND,
  LexicalEditor,
  PASTE_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { AutoLinkPlugin } from '@lexical/react/LexicalAutoLinkPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $createLinkNode,
  $isLinkNode,
  LinkNode,
  AutoLinkNode,
  TOGGLE_LINK_COMMAND,
} from '@lexical/link';
import { $getNearestNodeOfType, mergeRegister } from '@lexical/utils';
import { Bold, Italic, Underline, Link as LinkIcon } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  ALLOWED_IMAGE_MIME,
  MAX_IMAGE_BYTES,
  MAX_IMAGES_PER_COMMENT,
} from '@/redux/commet/comment.constants';
import {
  $createCommentImageNode,
  $isCommentImageNode,
  CommentImageNode,
} from './lexical-image-node';

const theme = {
  paragraph: 'mb-1',
  text: {
    bold: 'font-bold',
    italic: 'italic',
    underline: 'underline',
  },
  link: 'text-primary underline cursor-pointer',
};

const URL_REGEX =
  /((https?:\/\/(www\.)?)|(www\.))[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/;

const URL_REGEX_GLOBAL = new RegExp(URL_REGEX.source, 'g');

const normalizeUrl = (raw: string) =>
  raw.startsWith('http') ? raw : `https://${raw}`;

// Lexical throws "the editor state is empty" if given a serialized state whose
// root has no children. Stored comments can be empty-root states (or legacy
// non-Lexical content), so only use initialState when it parses to a non-empty
// root; otherwise fall back to undefined and let Lexical create a blank editor.
function hasNonEmptyRoot(initialState: string): boolean {
  try {
    return JSON.parse(initialState).root.children.length > 0;
  } catch {
    return false;
  }
}

function getSafeInitialState(initialState?: string): string | undefined {
  if (initialState && hasNonEmptyRoot(initialState)) return initialState;
  return undefined;
}

const MATCHERS = [
  (text: string) => {
    const match = URL_REGEX.exec(text);
    if (!match) return null;
    const fullMatch = match[0];
    return {
      index: match.index,
      length: fullMatch.length,
      text: fullMatch,
      url: normalizeUrl(fullMatch),
      attributes: { rel: 'noopener noreferrer', target: '_blank' },
    };
  },
];

export interface LexicalEditorHandle {
  clear: () => void;
  getSerializedState: () => string;
}

interface LexicalEditorProps {
  readOnly?: boolean;
  initialState?: string;
  placeholder?: string;
  onChange?: (serializedState: string) => void;
  className?: string;
  contentClassName?: string;
}

function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext();
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const savedSelectionRef = useRef<BaseSelection | null>(null);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) return;
    setIsBold(selection.hasFormat('bold'));
    setIsItalic(selection.hasFormat('italic'));
    setIsUnderline(selection.hasFormat('underline'));
    const node = selection.anchor.getNode();
    const parent = node.getParent();
    setIsLink($isLinkNode(parent) || $isLinkNode(node));
  }, []);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(updateToolbar);
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateToolbar();
          return false;
        },
        COMMAND_PRIORITY_LOW
      )
    );
  }, [editor, updateToolbar]);

  const insertLink = useCallback(() => {
    if (isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
      return;
    }
    let existingUrl = '';
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        savedSelectionRef.current = selection.clone();
        const node = selection.anchor.getNode();
        const parent = node.getParent();
        const linkNode = $getNearestNodeOfType(parent ?? node, LinkNode);
        if (linkNode) existingUrl = linkNode.getURL();
      } else {
        savedSelectionRef.current = null;
      }
    });
    setLinkUrl(existingUrl);
    setLinkDialogOpen(true);
  }, [editor, isLink]);

  const confirmLink = useCallback(() => {
    const raw = linkUrl.trim();
    if (!raw) return;
    const url = normalizeUrl(raw);
    const saved = savedSelectionRef.current;
    editor.update(() => {
      if (saved) $setSelection(saved.clone());
    });
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
    setLinkDialogOpen(false);
    setLinkUrl('');
    savedSelectionRef.current = null;
  }, [editor, linkUrl]);

  const btn = (active: boolean) =>
    cn(
      'p-1.5 rounded transition-colors',
      active
        ? 'bg-primary text-primary-foreground hover:bg-primary/90'
        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
    );

  return (
    <div className="flex items-center gap-1 border-b border-border px-2 py-1.5">
      <button
        type="button"
        className={btn(isBold)}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
        aria-label="Bold"
      >
        <Bold className="w-4 h-4" />
      </button>
      <button
        type="button"
        className={btn(isItalic)}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
        aria-label="Italic"
      >
        <Italic className="w-4 h-4" />
      </button>
      <button
        type="button"
        className={btn(isUnderline)}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')}
        aria-label="Underline"
      >
        <Underline className="w-4 h-4" />
      </button>
      <button
        type="button"
        className={btn(isLink)}
        onClick={insertLink}
        aria-label="Link"
      >
        <LinkIcon className="w-4 h-4" />
      </button>
      <Dialog
        open={linkDialogOpen}
        onOpenChange={(open) => {
          setLinkDialogOpen(open);
          if (!open) {
            setLinkUrl('');
            savedSelectionRef.current = null;
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Insert link</DialogTitle>
          </DialogHeader>
          <Input
            autoFocus
            type="url"
            placeholder="https://example.com"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                confirmLink();
              }
            }}
          />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setLinkDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={confirmLink}
              disabled={!linkUrl.trim()}
            >
              Insert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function countCommentImageNodes(editor: LexicalEditor): number {
  let count = 0;
  editor.getEditorState().read(() => {
    const root = $getRoot();
    const visit = (nodes: ReturnType<typeof root.getChildren>) => {
      for (const node of nodes) {
        if ($isCommentImageNode(node)) count += 1;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const children = (node as any).getChildren?.();
        if (Array.isArray(children)) visit(children);
      }
    };
    visit(root.getChildren());
  });
  return count;
}

function LinkPastePlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (event: ClipboardEvent) => {
        const clipboard = event.clipboardData;
        if (!clipboard) return false;
        if (clipboard.files && clipboard.files.length > 0) return false;
        const text = clipboard.getData('text/plain');
        if (!text) return false;
        const trimmed = text.trim();
        if (!trimmed) return false;

        const urlOnlyRegex = new RegExp(`^${URL_REGEX.source}$`);
        const isUrlOnly = urlOnlyRegex.test(trimmed);
        URL_REGEX_GLOBAL.lastIndex = 0;
        const hasUrl = URL_REGEX_GLOBAL.test(text);
        URL_REGEX_GLOBAL.lastIndex = 0;
        if (!isUrlOnly && !hasUrl) return false;

        event.preventDefault();
        event.stopPropagation();

        editor.update(() => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) return;

          if (isUrlOnly) {
            const url = normalizeUrl(trimmed);
            if (!selection.isCollapsed()) {
              editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
            } else {
              const linkNode = $createLinkNode(url, {
                rel: 'noopener noreferrer',
                target: '_blank',
              });
              linkNode.append($createTextNode(trimmed));
              selection.insertNodes([linkNode]);
            }
            return;
          }

          const nodes: Array<ReturnType<typeof $createTextNode>> = [];
          let cursor = 0;
          let match: RegExpExecArray | null;
          // eslint-disable-next-line no-cond-assign
          while ((match = URL_REGEX_GLOBAL.exec(text)) !== null) {
            const start = match.index;
            const end = start + match[0].length;
            if (start > cursor) {
              nodes.push($createTextNode(text.slice(cursor, start)));
            }
            const linkNode = $createLinkNode(normalizeUrl(match[0]), {
              rel: 'noopener noreferrer',
              target: '_blank',
            });
            linkNode.append($createTextNode(match[0]));
            // @ts-expect-error: link node accepted by insertNodes
            nodes.push(linkNode);
            cursor = end;
          }
          URL_REGEX_GLOBAL.lastIndex = 0;
          if (cursor < text.length) {
            nodes.push($createTextNode(text.slice(cursor)));
          }
          if (nodes.length > 0) selection.insertNodes(nodes);
        });

        return true;
      },
      COMMAND_PRIORITY_CRITICAL
    );
  }, [editor]);

  return null;
}

function ImagePastePlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (event: ClipboardEvent) => {
        const clipboard = event.clipboardData;
        if (!clipboard) return false;
        const imageFiles: File[] = [];
        for (let i = 0; i < clipboard.items.length; i += 1) {
          const item = clipboard.items[i];
          if (item.kind === 'file' && item.type.startsWith('image/')) {
            const file = item.getAsFile();
            if (file) imageFiles.push(file);
          }
        }
        if (imageFiles.length === 0) return false;
        event.preventDefault();

        const existing = countCommentImageNodes(editor);
        let remaining = MAX_IMAGES_PER_COMMENT - existing;
        if (remaining <= 0) {
          toast.error(
            `A comment can include at most ${MAX_IMAGES_PER_COMMENT} images`
          );
          return true;
        }

        for (const file of imageFiles) {
          if (remaining <= 0) {
            toast.error(
              `A comment can include at most ${MAX_IMAGES_PER_COMMENT} images`
            );
            break;
          }
          if (!ALLOWED_IMAGE_MIME.has(file.type)) {
            toast.error(`${file.type || 'This file type'} is not supported`);
            continue;
          }
          if (file.size > MAX_IMAGE_BYTES) {
            toast.error('Image must be 5 MB or smaller');
            continue;
          }
          remaining -= 1;
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result;
            if (typeof result !== 'string') return;
            editor.update(() => {
              $insertNodes([
                $createCommentImageNode({
                  attachmentId: '',
                  src: result,
                  altText: file.name || 'image',
                }),
              ]);
            });
          };
          reader.readAsDataURL(file);
        }
        return true;
      },
      COMMAND_PRIORITY_LOW
    );
  }, [editor]);

  return null;
}

function EditorRefPlugin({
  editorRef,
}: {
  editorRef: React.MutableRefObject<LexicalEditor | null>;
}) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    // eslint-disable-next-line no-param-reassign
    editorRef.current = editor;
    return () => {
      // eslint-disable-next-line no-param-reassign
      editorRef.current = null;
    };
  }, [editor, editorRef]);
  return null;
}

const Editor = forwardRef<LexicalEditorHandle, LexicalEditorProps>(
  (
    {
      readOnly = false,
      initialState,
      placeholder = '',
      onChange,
      className,
      contentClassName,
    },
    ref
  ) => {
    const editorRef = useRef<LexicalEditor | null>(null);

    useImperativeHandle(ref, () => ({
      clear: () => {
        const editor = editorRef.current;
        if (!editor) return;
        editor.update(() => {
          const root = $getRoot();
          root.clear();
          root.append($createParagraphNode());
        });
      },
      getSerializedState: () => {
        const editor = editorRef.current;
        if (!editor) return '';
        return JSON.stringify(editor.getEditorState().toJSON());
      },
    }));

    const initialConfig = {
      namespace: 'CommentEditor',
      theme,
      onError(error: Error) {
        // eslint-disable-next-line no-console
        console.error(error);
      },
      editable: !readOnly,
      nodes: [LinkNode, AutoLinkNode, CommentImageNode],
      editorState: getSafeInitialState(initialState),
    };

    const handleChange = (editorState: EditorState) => {
      if (!onChange) return;
      onChange(JSON.stringify(editorState.toJSON()));
    };

    return (
      <LexicalComposer initialConfig={initialConfig}>
        <div className={cn('relative flex flex-col', className)}>
          {!readOnly && <ToolbarPlugin />}
          <div className="relative flex-1">
            <RichTextPlugin
              contentEditable={
                <ContentEditable
                  className={cn(
                    'outline-none px-3 py-2 min-h-[80px] resize-none text-sm',
                    contentClassName
                  )}
                />
              }
              placeholder={
                <div className="pointer-events-none absolute left-3 top-2 text-sm text-muted-foreground">
                  {placeholder}
                </div>
              }
              ErrorBoundary={LexicalErrorBoundary}
            />
          </div>
          <HistoryPlugin />
          <LinkPlugin />
          <AutoLinkPlugin matchers={MATCHERS} />
          {!readOnly && <LinkPastePlugin />}
          {!readOnly && <ImagePastePlugin />}
          {!readOnly && onChange && (
            <OnChangePlugin onChange={handleChange} ignoreSelectionChange />
          )}
          <EditorRefPlugin editorRef={editorRef} />
        </div>
      </LexicalComposer>
    );
  }
);

Editor.displayName = 'LexicalEditor';

export default Editor;
